import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Copy, Check, Link2, Link2Off, Share2 } from "lucide-react";
import { safeFormatDate } from "@/lib/dates";

/**
 * Generate + manage a 7-day public share link for a horse's ranking
 * report. The buyer opens it without a dashboard login.
 *
 * The backend returns a token; we build the shareable URL from the
 * CURRENT origin so preview + production links always point at the
 * right host (the backend default is production-only).
 */
export function ShareHorseDialog({ horse, open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [link, setLink] = useState(null); // { token, url, expires_at }
  const [copied, setCopied] = useState(false);
  const [revoking, setRevoking] = useState(false);

  const shareUrl = link ? `${window.location.origin}/share/horse/${link.token}` : "";

  const generate = async () => {
    if (!horse?.id || loading) return;
    setLoading(true);
    try {
      const res = await api.post(`/horses/${horse.id}/share`);
      setLink(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create share link");
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch (_e) {
      toast.error("Could not copy — select and copy the link manually");
    }
  };

  const revoke = async () => {
    if (!link?.token || revoking) return;
    setRevoking(true);
    try {
      await api.delete(`/horses/share/${link.token}`);
      toast.success("Link revoked");
      setLink(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not revoke link");
    } finally {
      setRevoking(false);
    }
  };

  const handleOpenChange = (v) => {
    if (!v) { setLink(null); setCopied(false); }
    onOpenChange?.(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="share-horse-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-4 w-4 text-[hsl(var(--ap-brass))]" />
            Share {horse?.name}&rsquo;s report
          </DialogTitle>
          <DialogDescription>
            Create a private link a buyer can open without a login. It shows the full ranking report and lets them download the PDF. The link expires after 7 days and you can revoke it anytime.
          </DialogDescription>
        </DialogHeader>

        {!link ? (
          <div className="py-4">
            <Button
              onClick={generate}
              disabled={loading}
              className="w-full bg-[hsl(var(--ap-forest))] hover:bg-[hsl(var(--ap-forest))]/90"
              data-testid="share-horse-generate"
            >
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Link2 className="mr-2 h-4 w-4" />}
              Generate share link
            </Button>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="flex items-center gap-2">
              <Input
                readOnly
                value={shareUrl}
                onFocus={(e) => e.target.select()}
                className="font-mono text-xs"
                data-testid="share-horse-url"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={copy}
                aria-label="Copy link"
                data-testid="share-horse-copy"
              >
                {copied ? <Check className="h-4 w-4 text-[hsl(var(--ap-forest))]" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            {link.expires_at && (
              <p className="text-xs text-muted-foreground" data-testid="share-horse-expiry">
                Expires {safeFormatDate(link.expires_at, "d MMM yyyy, HH:mm")}
              </p>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          {link ? (
            <Button
              type="button"
              variant="ghost"
              onClick={revoke}
              disabled={revoking}
              className="text-destructive hover:text-destructive"
              data-testid="share-horse-revoke"
            >
              {revoking ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Link2Off className="mr-2 h-4 w-4" />}
              Revoke link
            </Button>
          ) : <span />}
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} data-testid="share-horse-close">
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
